import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaFilePdf, FaFileWord, FaFileExcel, FaFileAlt, FaImage,
  FaTrash, FaUpload, FaPhone, FaEnvelope, FaBuilding,
  FaUser, FaMapMarkerAlt, FaBoxes, FaCheckCircle,
} from "react-icons/fa";
import { baseurl } from "../config/url";

/* Helpers */
const fileIconFor = (name = "", mime = "") => {
  const n = name.toLowerCase();
  if (mime.startsWith("image/")) return <FaImage className="text-blue-500" />;
  if (n.endsWith(".pdf")) return <FaFilePdf className="text-red-600" />;
  if (n.endsWith(".doc") || n.endsWith(".docx")) return <FaFileWord className="text-blue-600" />;
  if (n.endsWith(".xls") || n.endsWith(".xlsx")) return <FaFileExcel className="text-green-600" />;
  return <FaFileAlt className="text-slate-500" />;
};

const humanSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ACCEPTED_MIME = [
  "image/jpeg", "image/png", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 50 * 1024 * 1024;

/* ✨ Reset template */
const INITIAL_FORM = {
  fullName: "",
  email: "",
  phone: "",
  company: "",
  city: "",
  pincode: "",
  quantity: "",
  preferredBrands: "",
  requirement: "",
  agree: false,
};

const BulkRequest = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);       // File[]
  const [previews, setPreviews] = useState([]); // { url, name, size, type, isImage }[]
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const totalSize = useMemo(
    () => files.reduce((s, f) => s + (f?.size || 0), 0),
    [files]
  );

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((p) => p?.url?.startsWith("blob:") && URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setErrors((er) => ({ ...er, [name]: "" }));
  };

  const validate = () => {
    const er = {};
    if (!form.fullName.trim()) er.fullName = "Name is required";
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.phone.trim())) er.phone = "Valid phone is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Valid email is required";
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) er.pincode = "Pincode must be 6 digits";
    if (!form.requirement.trim()) er.requirement = "Please describe your requirement";
    if (!form.agree) er.agree = "Please accept terms to proceed";
    if (files.length > MAX_FILES) er.files = `Max ${MAX_FILES} files allowed`;
    if (totalSize > MAX_TOTAL_SIZE) er.files = `Total size must be under ${humanSize(MAX_TOTAL_SIZE)}`;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const pickFiles = (picked) => {
    const incoming = Array.from(picked || []);
    if (!incoming.length) return;

    const valid = [];
    for (const f of incoming) {
      const okType = ACCEPTED_MIME.includes(f.type);
      const okSize = f.size <= MAX_FILE_SIZE;
      if (!okType || !okSize) continue;
      valid.push(f);
    }

    const combined = [...files, ...valid].slice(0, MAX_FILES);
    setFiles(combined);

    const makePreview = (f) => ({
      url: f.type.startsWith("image/") ? URL.createObjectURL(f) : "",
      name: f.name,
      size: f.size,
      type: f.type,
      isImage: f.type.startsWith("image/"),
    });

    const newPreviews = [...previews, ...valid.map(makePreview)].slice(0, MAX_FILES);
    setPreviews(newPreviews);
    setStatus({ type: "", msg: "" });
    setErrors((er) => ({ ...er, files: "" }));
  };

  const onFileInputChange = (e) => pickFiles(e.target.files);

  const onDrop = (e) => {
    e.preventDefault();
    pickFiles(e.dataTransfer.files);
  };

  const onRemoveFile = (idx) => {
    const copyFiles = [...files];
    const copyPrev = [...previews];
    const removed = copyPrev[idx];

    copyFiles.splice(idx, 1);
    copyPrev.splice(idx, 1);

    if (removed?.url && removed.url.startsWith("blob:")) URL.revokeObjectURL(removed.url);

    setFiles(copyFiles);
    setPreviews(copyPrev);
  };

  /* ✨ Reset everything after success */
  const resetFormCompletely = () => {
    previews.forEach((p) => p?.url?.startsWith("blob:") && URL.revokeObjectURL(p.url));
    setFiles([]);
    setPreviews([]);
    setForm(INITIAL_FORM);
    setErrors({});
    if (inputRef.current) inputRef.current.value = null; // clears file input
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    if (!validate()) {
      setStatus({ type: "error", msg: "Please fix the highlighted fields." });
      return;
    }

    try {
      setSubmitting(true);

      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));

      const res = await axios.post(`${baseurl}/api/bulk-request`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.data) {
        setStatus({
          type: "success",
          msg: res?.data?.message || "Your request has been submitted successfully!",
        });

        // ✅ Clear the form completely
        resetFormCompletely();
      }
    } catch (err) {
      setStatus({
        type: "error",
        msg: err?.response?.data?.message || "Failed to submit. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="w-full">
      {/* Header Banner */}
      <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-12 text-white">
          <h1 className="text-xl sm:text-3xl font-semibold">Request Quote for Bulk</h1>
          <p className="mt-2 text-xs sm:text-sm text-white/90 max-w-2xl">
            Receive quality quotes of products &amp; materials from multiple sellers in less than a day.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-white/25 bg-white/15 px-3 py-1 text-[11px] sm:text-xs">
              Get responses in 24 hours
            </span>
            <span className="inline-flex items-center rounded-full border border-white/25 bg-white/15 px-3 py-1 text-[11px] sm:text-xs">
              Receive up to 115 quotes
            </span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Contact Card */}
          <aside className="lg:col-span-1">
            <div className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-slate-50 px-5 py-4 border-b border-slate-200">
                <h2 className="text-sm sm:text-base font-semibold text-slate-800">Contact Us</h2>
              </div>
              <div className="p-5 text-slate-700 text-xs sm:text-sm space-y-2">
                <div className="flex items-start gap-3">
                  <FaBuilding className="mt-1 text-indigo-600" />
                  <div>
                    <p className="font-medium text-slate-900">OGCS Private Limited</p>
                    <p>No 03, C-6, 14, Rd Number 2</p>
                    <p>Sector 18, New Panvel, Dist: Raigad</p>
                    <p>Pin Code: 410206</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <FaPhone className="text-emerald-600" />
                  <a href="tel:+919967610135" className="hover:underline">
                    +91 9967610135
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-blue-600" />
                  {/* ✅ Email shows the same on mobile & desktop */}
                  <a href="mailto:info@ogcs.co.in" className="hover:underline">
                    info@ogcs.co.in
                  </a>
                </div>
                <div className="mt-4 rounded-lg bg-indigo-50 border border-indigo-100 p-3 text-indigo-900">
                  <p className="text-[11px] sm:text-xs">
                    Reach out if you have any questions. Our team will assist you with bulk orders and quotations.
                  </p>
                </div>
              </div>
            </div>
          </aside>

          {/* Right: Form Card */}
          <section className="lg:col-span-2">
            <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden" noValidate>
              <div className="bg-slate-50 px-5 py-4 border-b border-slate-200">
                <h2 className="text-sm sm:text-base font-semibold text-slate-800 flex items-center gap-2">
                  <FaBoxes /> Bulk Request Form
                </h2>
                <p className="text-[11px] sm:text-xs text-slate-600 mt-1">
                  Share your requirements and (optionally) attach drawings, BOQs, or references.
                </p>
              </div>

              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Full Name *</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="fullName"
                      value={form.fullName}
                      onChange={onChange}
                      className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        errors.fullName ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                      }`}
                      placeholder="Enter your name"
                    />
                    <FaUser className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                  {errors.fullName && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{errors.fullName}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Mobile Number *</label>
                  <div className="relative">
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        errors.phone ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                      }`}
                      placeholder="+91 9XXXXXXXXX"
                    />
                    <FaPhone className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                  {errors.phone && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{errors.phone}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Email *</label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        errors.email ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                      }`}
                      placeholder="name@example.com"
                    />
                    <FaEnvelope className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                  {errors.email && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{errors.email}</p>}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Company / Organization</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={onChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Company name (optional)"
                    />
                    <FaBuilding className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>

                {/* City */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">City</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={onChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Delivery city"
                    />
                    <FaMapMarkerAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>

                {/* Pincode */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Pincode</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="pincode"
                      value={form.pincode}
                      onChange={onChange}
                      maxLength={6}
                      className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                        errors.pincode ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                      }`}
                      placeholder="6-digit pincode"
                    />
                    <FaMapMarkerAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                  {errors.pincode && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{errors.pincode}</p>}
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Quantity / Volume</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="quantity"
                      value={form.quantity}
                      onChange={onChange}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., 500 bags cement, 10 MT TMT, etc."
                    />
                    <FaBoxes className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>

                {/* Preferred Brands */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Preferred Brands (optional)</label>
                  <input
                    type="text"
                    name="preferredBrands"
                    value={form.preferredBrands}
                    onChange={onChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., UltraTech, JSW, TATA, ACC, Dr. Fixit..."
                  />
                </div>

                {/* Requirement */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Requirement Details *</label>
                  <textarea
                    name="requirement"
                    value={form.requirement}
                    onChange={onChange}
                    rows={4}
                    className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      errors.requirement ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                    }`}
                    placeholder="Describe your requirement, site details, delivery timeline, etc."
                  />
                  {errors.requirement && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{errors.requirement}</p>}
                </div>

                {/* File Upload */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Attach Files (Images / PDF / DOC / XLS)</label>
                  <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 text-center ${
                      errors.files ? "border-red-400 bg-red-50" : "border-slate-300 bg-slate-50"
                    }`}
                  >
                    <FaUpload className="text-2xl sm:text-3xl text-slate-500" />
                    <div className="text-[11px] sm:text-xs text-slate-600">
                      Drag &amp; drop files here, or{" "}
                      <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="ml-1 text-blue-600 hover:underline"
                      >
                        browse
                      </button>
                    </div>
                    <input
                      ref={inputRef}
                      type="file"
                      multiple
                      accept={ACCEPTED_MIME.join(",")}
                      className="hidden"
                      onChange={onFileInputChange}
                    />
                    <div className="text-[10px] sm:text-xs text-slate-500">
                      Up to {MAX_FILES} files • {humanSize(MAX_FILE_SIZE)} each • Total under {humanSize(MAX_TOTAL_SIZE)}
                    </div>
                  </div>

                  {errors.files && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{errors.files}</p>}

                  {!!previews.length && (
                    <ul className="mt-3 divide-y rounded-lg border border-slate-200 overflow-hidden">
                      {previews.map((p, i) => (
                        <li key={`${p.name}-${i}`} className="flex items-center gap-3 p-3">
                          <div className="w-12 h-12 flex items-center justify-center rounded bg-slate-100 overflow-hidden">
                            {p.isImage ? (
                              <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                              fileIconFor(p.name, p.type)
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-slate-900 truncate">{p.name}</p>
                            <p className="text-[11px] sm:text-xs text-slate-500">{humanSize(p.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => onRemoveFile(i)}
                            className="inline-flex items-center gap-1 text-xs sm:text-sm text-red-600 hover:text-red-700"
                            title="Remove"
                          >
                            <FaTrash /> Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Terms */}
                <div className="md:col-span-2">
                  <label className="inline-flex items-start gap-2 text-[11px] sm:text-xs text-slate-700">
                    <input
                      type="checkbox"
                      name="agree"
                      checked={form.agree}
                      onChange={onChange}
                      className="mt-0.5"
                    />
                    <span>
                      I confirm that the details provided are correct and I agree to be contacted by OGCS / BuildPro-OGCS
                      and verified sellers for quotation purposes.
                    </span>
                  </label>
                  {errors.agree && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{errors.agree}</p>}
                </div>
              </div>

              {/* Status */}
              {status.msg && (
                <div
                  role="status"
                  aria-live="polite"
                  className={`mx-5 mb-4 rounded-lg px-4 py-3 text-xs sm:text-sm ${
                    status.type === "success"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : status.type === "error"
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-slate-50 text-slate-700 border border-slate-200"
                  }`}
                >
                  {status.type === "success" ? (
                    <span className="inline-flex items-center gap-2">
                      <FaCheckCircle /> {status.msg}
                    </span>
                  ) : (
                    status.msg
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="px-5 pb-6 flex items-center justify-end gap-3">
                <Link
                  to="/"
                  className="rounded-lg border border-slate-300 px-4 py-2 text-xs sm:text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </section>
        </div>
      </section>
    </main>
  );
};

export default BulkRequest;
